// DotByDotPreference — 「ドットバイドット表示」(自動 | しない) を端末ごとに
// localStorage へ永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/canvas-dot-by-dot-2026-10-28.md): 画面の拡大率 (dpr) が 1 より大きく 2 未満の
// とき、既定 ('auto') ではキャンバス 1px を画面 1 ドットで表示する。急に絵が小さく見えて
// 困る人が従来表示 ('off') に戻せるようにする設定.
//
// 設計は aaPreference.ts / stabilizerPreference.ts と同流儀: 不正値 / 不在 / storage 不可は
// 既定 ('auto')、例外を throw しない. 値は `infrastructure/drawing/zoom.ts` の `DotByDotMode`.

import { DEFAULT_DOT_BY_DOT_MODE, type DotByDotMode } from './drawing/zoom.js';

/** localStorage キー. */
export const DOT_BY_DOT_KEY = 'oekaki:dot-by-dot';

export const isDotByDotMode = (v: unknown): v is DotByDotMode => v === 'auto' || v === 'off';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface DotByDotPreferenceStore {
  /** 不在 / 不正 / storage 不可 → 既定 ('auto'). */
  get(): DotByDotMode;
  /** 保存. storage 不可なら no-op. */
  set(mode: DotByDotMode): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:dot-by-dot__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    return got === '1' ? ls : null;
  } catch {
    return null;
  }
};

export const createDotByDotPreferenceStore = (
  opts: { storage?: StorageLike | null } = {},
): DotByDotPreferenceStore => {
  const storage = opts.storage === null ? null : (opts.storage ?? probeBrowserStorage());
  return {
    get() {
      if (storage === null) return DEFAULT_DOT_BY_DOT_MODE;
      try {
        const raw = storage.getItem(DOT_BY_DOT_KEY);
        return isDotByDotMode(raw) ? raw : DEFAULT_DOT_BY_DOT_MODE;
      } catch {
        return DEFAULT_DOT_BY_DOT_MODE;
      }
    },
    set(mode) {
      if (storage === null) return;
      try {
        storage.setItem(DOT_BY_DOT_KEY, mode);
      } catch {
        // ignore
      }
    },
  };
};
